import { Component, OnInit, Input, Output, SimpleChanges, OnChanges, EventEmitter, ContentChild, ContentChildren, TemplateRef, QueryList } from '@angular/core';
import { GridHeaderModel, GridRowModel, GridContainerStyle } from '../../models/grid'
import { isNil } from 'lodash-es';
import { ButtonModel, ValueControlModel } from '../../models/control';
import { ToolModel } from '../../models/tool';
import { ExpandableRowDef, GridColumnDef } from '../grid.component';
import { AgGridCustomRowExpandCellRenderer } from './ag-grid-expand.component';
import { AgGridCustomStatusBarPaginationComponent } from './ag-grid-status-paging.component';
import { AgGridCustomCellRenderer } from './ag-grid-cell-renderer.component';
import { AgGridCustomCellEditor } from './ag-grid-cell-editor.component';
import { AgGridCustomCommandCellEditor } from './ag-grid-command.component';
import { AgGridCustomStatusBarAddLineComponent } from './ag-grid-status-add.component';

import { CellClickedEvent, ColDef, SelectionChangedEvent, SizeColumnsToContentStrategy, SizeColumnsToFitGridStrategy, SizeColumnsToFitProvidedWidthStrategy, StatusPanelDef, ModuleRegistry, RowSelectionOptions, GetRowIdFunc, GetRowIdParams, GridReadyEvent, GridApi, Column, RowNode, SortChangedEvent, FilterChangedEvent, RowStyle, SelectionColumnDef } from '@ag-grid-community/core';

import { MenuModule } from '@ag-grid-enterprise/menu';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { MasterDetailModule } from "@ag-grid-enterprise/master-detail";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { StatusBarModule } from '@ag-grid-enterprise/status-bar';
import { AgGridRowOptionsCellRenderer } from './ag-grid-row-options.component';
import { AgGridFilterComponent } from './ag-grid-filter.component';


// export enum ELoadingStatus {
//   Loading,
//   Loaded,
//   NoResults,
//   Error
// }

// @Directive({
//   selector: '[gridCellDisplayControlDef]'
// })
// export class GridCellDisplayControlDef {
//   constructor(public template: TemplateRef<any>) { }
// }

// @Directive({
//   selector: '[gridCellEditControlDef]'
// })
// export class GridCellEditControlDef {
//   constructor(public template: TemplateRef<any>) { }
// }

// @Directive({
//   selector: '[expandableRowDef]'
// })
// export class ExpandableRowDef {
//   constructor(public template: TemplateRef<any>) { }
// }

// @Directive({
//   selector: '[gridColumnDef]'
// })
// export class GridColumnDef {
//   @Input('gridColumnDef')
//   get id(): string {
//     return this._id;
//   }
//   set id(value: string) {
//     this._id = value;
//   }
//   protected _id: string;

//   @ContentChild(GridCellDisplayControlDef) displayControlDef: GridCellDisplayControlDef;
//   @ContentChild(GridCellEditControlDef) editControlDef: GridCellEditControlDef;

//   constructor() {
//   }
// }

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  MasterDetailModule,
  StatusBarModule,
  MenuModule,
  ColumnsToolPanelModule
]);


export class AgGridContainerStyle extends GridContainerStyle {
  private _gridApi: GridApi;
  set gridApi(api: GridApi) {
    this._gridApi = api;
    if (this._forceSetHeaderSize) {
      this.updateColumnSize();
    }
  }

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
      type: "fitGridWidth",
      defaultMinWidth: 100
    };

  private _forceSetHeaderSize = false;

  constructor(columnSizingType: string, classes?: string[], style?: { [klass: string]: any }) {
    super(columnSizingType, classes, style);
    if (columnSizingType === 'headersWidth') {
      this.autoSizeStrategy = {
        type: 'fitGridWidth'
      }
    } else if (columnSizingType === 'fixedWidth') {
      this.autoSizeStrategy = null;
    } else if (columnSizingType === 'fitedWidth') {
      this.autoSizeStrategy = {
        type: 'fitGridWidth'
      }
    } else if (columnSizingType === 'cellsWidth') {
      this.autoSizeStrategy = {
        type: 'fitCellContents'
      }
    }
  }

  override setHeadersWidthClass() {
    this.autoSizeStrategy = {
      type: 'fitGridWidth'
    }
    this.updateColumnSize();
  }

  override setFixedWidthClass() {
    this.autoSizeStrategy = null;
  }

  override setFittedWidthClass() {
    this.autoSizeStrategy = {
      type: 'fitGridWidth'
    }
    this.updateColumnSize();
  }

  override setCellsWidthClass() {
    this.autoSizeStrategy = {
      type: 'fitCellContents'
    }
    this.updateColumnSize();
  }

  private updateColumnSize() {
    if (this.autoSizeStrategy) {
      if (this._gridApi) {
        if (this.autoSizeStrategy.type === 'fitCellContents') {
          this._gridApi.autoSizeAllColumns();
        } else {
          this._gridApi.sizeColumnsToFit();
        }
        this._forceSetHeaderSize = false;
      } else {
        this._forceSetHeaderSize = true;
        console.warn(`Unable to resize column because gridApi not set`);
      }
    }
  }
}

export class AgGridHeaderModel<TFC extends ValueControlModel | void = void> extends GridHeaderModel<TFC> {
  private _gridApi: GridApi;
  set gridApi(api: GridApi) {
    this._gridApi = api;
    if (this._forceSetHidden) {
      this.hidden = this.hidden;
    }
    if (this._forceSetWidth) {
      this.width = this.width;
    }
  }

  private _forceSetHidden = false;
  override get hidden(): boolean {
    return super.hidden;
  }
  override set hidden(val: boolean) {
    super.hidden = val
    if (this._gridApi) {
      this._gridApi.setColumnsVisible([this.id], !val);
      this._forceSetHidden = false;
    } else {
      this._forceSetHidden = true;
      console.warn(`Unable to set visibility for column '${this.name}' (ID: ${this.id}) because gridApi is not yet available. Requested visibility: ${val}`
      );
    }
  }

  private _forceSetWidth = false;
  override get width(): number {
    return super.width;
  }
  override set width(val: number) {
    super.width = val;
    if (this._gridApi) {
      this._gridApi.setColumnWidths([{ key: this.id, newWidth: val }]);
      this._forceSetWidth = false;
    } else {
      this._forceSetWidth = true;
      console.warn(`Unable to set width for column '${this.name}' (ID: ${this.id}) because gridApi is not yet available. Requested visibility: ${val}`
      );
    }
  }
}

export class AgGridRowModel extends GridRowModel {
  gridApi: GridApi;

  override setDisplayMode() {
    super.setDisplayMode();
    this.gridApi.stopEditing();
  }

  override setEditMode() {
    super.setEditMode();

    const rowIndex = this.gridApi.getRowNode(this.rowId)?.rowIndex;
    if (rowIndex !== -1) {
      const firstEditableCell = this.getFirstEditableCell();
      const cellId = Object.keys(this.cells).find(c => this.cells[c] === firstEditableCell)
      this.gridApi.startEditingCell(
        {
          rowIndex: rowIndex,
          colKey: cellId
        }
      )
    } else {
      console.warn(`row setEditMode was called but no row found with id ${this.rowId}`);
    }
  }

  override getFirstEditableCell() {
    const columns = this.gridApi.getColumns();
    for (let column of columns) {
      const cell = this.cells[column.getId()];
      if (cell?.isEditable && column.isVisible()) {
        return cell;
      }
    }
    return null;
  }

  override getLastEditableCell() {
    let columns = [...this.gridApi.getColumns()].reverse();
    for (let column of columns) {
      const cell = this.cells[column.getId()];
      if (cell?.isEditable && column.isVisible()) {
        return cell;
      }
    }
    return null;
  }
}

@Component({
  standalone: false,
  selector: 'datex-ag-grid',
  templateUrl: './ag-grid.component.html',
  styleUrls: ['./ag-grid.component.css'],
})
export class AgGridComponent implements OnInit, OnChanges {

  private gridApi!: GridApi;

  @Input() pageSize: number = 10;
  @Input() pageSkip: number = 0;
  @Input() totalCount: number = 0;
  @Input() selection: boolean = false;
  @Input() headers: { [id: string]: AgGridHeaderModel<ValueControlModel | void> };
  @Input() rows: AgGridRowModel[];
  @Input() canEdit: boolean = false;
  @Input() canAdd: boolean = false;
  @Input() addNewRowFn: (() => Promise<AgGridRowModel>) | null
  @Input() addLineModel: ToolModel<ButtonModel>;
  @Input() rowCanExpand: boolean = false;
  @Input() rowCommandTemplateRef: TemplateRef<any>;

  @Input() containerStyles: AgGridContainerStyle;

  @Output() pageChange: EventEmitter<void> = new EventEmitter();
  @Output() pageSizeChange: EventEmitter<number> = new EventEmitter();
  @Output() pageSkipChange: EventEmitter<number> = new EventEmitter();
  @Output() selectionChange: EventEmitter<any[]> = new EventEmitter();
  @Output() sortChange: EventEmitter<void> = new EventEmitter();
  @Output() filterChange: EventEmitter<void> = new EventEmitter();

  @ContentChildren(GridColumnDef, { descendants: true }) _contentColumnDefs: QueryList<GridColumnDef>;
  _columnDefsByName = new Map<string, GridColumnDef>();

  @ContentChild(ExpandableRowDef) expandableRowDef: ExpandableRowDef;

  // public ELoadingStatus = ELoadingStatus;

  detailCellRenderer: any = AgGridCustomRowExpandCellRenderer;
  detailCellRendererParams = { grid: this };

  pagesCount = 0;
  currentPage = 1;
  currentPageFrom = 0;
  currentPageTo = 0;
  // lastClickedIndex: number = null;

  headersOrder: string[] = [];

  // private rowsPropertyChangesSubscription: Subscription;

  // @Output() exitFromLastCell: EventEmitter<void> = new EventEmitter();

  public statusBar: {
    statusPanels: StatusPanelDef[];
  } = {
      statusPanels: [
        {
          statusPanel: AgGridCustomStatusBarAddLineComponent,
          align: 'left',
          statusPanelParams: {
            grid: this
          }
        },
        {
          statusPanel: AgGridCustomStatusBarPaginationComponent,
          align: 'right',
          statusPanelParams: {
            grid: this
          }
        }
      ],
    };

  agColDefs: ColDef[] = [];
  agSelection: RowSelectionOptions;
  agSelectionColumnDef: SelectionColumnDef;
  rowHeight: number = 40;

  constructor() {

  }

  onAgGridReady(params: GridReadyEvent) {
    // console.log('onAgGridReady')
    this.gridApi = params.api;

    if (this.rows) {
      this.rows.forEach(r => r.gridApi = this.gridApi);
    }

    if (this.headers) {
      Object.keys(this.headers).forEach(hk => { this.headers[hk].gridApi = this.gridApi });
    }

    if (this.containerStyles) {
      this.containerStyles.gridApi = this.gridApi;
    }

    if (this.containerStyles.classes.includes('default')) {
      this.rowHeight = 40;
    }

    if (this.containerStyles.classes.includes('compact')) {
      this.rowHeight = 34;
    }

    if (this.containerStyles.classes.includes('relaxed')) {
      this.rowHeight = 52;
    }
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rows']) {
      // console.log('ngOnChanges rows')
      // if (this.rowsPropertyChangesSubscription) {
      //   this.rowsPropertyChangesSubscription.unsubscribe();
      // }

      // if (this.rows?.length) {
      //   const rowsPropertyChangesObservables = this.rows.map(t => t.$propertyChanges);
      //   this.rowsPropertyChangesSubscription = merge(...rowsPropertyChangesObservables).subscribe((property: string) => {
      //     if (this.selection && property === 'selected') {
      //       this.updateSelection();
      //     }
      //   });
      // }

      // if (this.selection) {
      //   this.updateSelection();
      // }

      this.rows.forEach(r => r.gridApi = this.gridApi);
    }
    if (changes['headers']) {
      // console.log('ngOnChanges headers')
      this.headersOrder = [];
      if (this.headers) {
        Object.keys(this.headers).forEach(hk => { this.headersOrder.push(hk); });
        Object.keys(this.headers).forEach(hk => { this.headers[hk].gridApi = this.gridApi });
      }
    }
    if (changes['container']) {
      // console.log('ngOnChanges containers')
      if (this.containerStyles) {
        this.containerStyles.gridApi = this.gridApi;
      }
    }

    if (changes['totalCount'] || changes['pageSize'] || changes['pageSkip']) {
      this.updatePagination();
    }
  }

  ngAfterContentInit() {
    this._cacheColumnDefs();
    this._initializeAgGridOptions();
  }

  ngOnDestroy() {
    this._columnDefsByName.clear();
    // if (this.rowsPropertyChangesSubscription) {
    //   this.rowsPropertyChangesSubscription.unsubscribe();
    // }
  }

  private _cacheColumnDefs() {
    this._columnDefsByName.clear();

    this._contentColumnDefs.forEach(columnDef => {
      if (this._columnDefsByName.has(columnDef.id)) {
        throw new Error(`Duplicate column definition for ${columnDef.id}`);
      }
      this._columnDefsByName.set(columnDef.id, columnDef);
    });
  }

  private _initializeAgGridOptions() {

    if (this.containerStyles.classes.includes('default')) {
      this.rowHeight = 40;
    }

    if (this.containerStyles.classes.includes('compact')) {
      this.rowHeight = 34;
    }

    if (this.containerStyles.classes.includes('relaxed')) {
      this.rowHeight = 52;
    }

    if (this.rowCanExpand) {
      // add a expand icon column
      this.agColDefs.push({
        maxWidth: 26,
        minWidth: 26,
        resizable: false,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
        suppressHeaderFilterButton: true,
        suppressFloatingFilterButton: true,
        suppressMovable: true,
        cellRenderer: 'agGroupCellRenderer'
      });
    }

    if (this.selection) {
      this.agSelection = {
        mode: "multiRow",
        headerCheckbox: true,
        enableClickSelection: false,
      };
      this.agSelectionColumnDef = {
        pinned: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
      };
    }

    var isCellEditable = (columnDef: GridColumnDef) => this.canEdit && !isNil(columnDef.editControlDef);
    this._contentColumnDefs.forEach(columnDef => {
      this.agColDefs.push(
        {
          headerName: this.headers[columnDef.id].name,
          colId: columnDef.id,
          sortable: this.headers[columnDef.id].sortable,
          comparator: (valueA, valueB, nodeA, nodeB, isDescending: boolean) => 0,
          headerClass: this.headers[columnDef.id].required ? `ag-required` : ``,
          cellClass: isCellEditable(columnDef) ? 'outline-border' : '',
          cellRenderer: AgGridCustomCellRenderer,
          cellRendererParams: {
            columnDef: columnDef
          },
          editable: () => isCellEditable(columnDef),
          cellEditor: AgGridCustomCellEditor,
          cellEditorParams: {
            columnDef: columnDef
          },
          onCellClicked: (ev: CellClickedEvent) => {
            this.onCellClick(ev.colDef.colId, ev.data, ev.rowIndex);
          },
          suppressKeyboardEvent: (params) => {
            // Suppress the Enter key triggering edit mode
            if (params.event.key === 'Enter') {
              return true;
            }
            // Suppress the Escape key triggering exit edit mode
            else if (params.event.key === 'Escape') {
              return true;
            }
            return false; // Allow other keys
          },
          width: this.containerStyles.columnSizingType === 'fixedWidth' ? this.headers[columnDef.id].width : null
          , filter: this.headers[columnDef.id].filterable ? AgGridFilterComponent : null
          , filterParams: this.headers[columnDef.id].filterable ? {
            controlDef: columnDef.filterControlDef,
            header: this.headers[columnDef.id]
          } : null
        }
      )
    });

    // command column
    if (this.canEdit) {
      const columnSize = this.rowHeight * 2;
      this.agColDefs.push(
        {
          maxWidth: columnSize,
          minWidth: columnSize,
          headerName: '',
          colId: '$command',
          pinned: 'right',
          suppressHeaderMenuButton: true,
          resizable: false,
          editable: true,
          cellEditor: AgGridCustomCommandCellEditor,
          cellEditorParams: {
            grid: this
          },
          hide: true,
          sortable: false
        }
      );
    }

    if (this.rowCommandTemplateRef) {
      const columnSize = this.rowHeight;
      this.agColDefs.push(
        {
          maxWidth: columnSize,
          minWidth: columnSize,
          headerName: '',
          colId: '$rowOptions',
          pinned: 'right',
          suppressHeaderMenuButton: true,
          resizable: false,
          editable: false,
          cellRenderer: AgGridRowOptionsCellRenderer,
          cellRendererParams: {
            rowCommandTemplateRef: this.rowCommandTemplateRef
          },
          hide: false,
          sortable: false
        }
      );
    }
  }

  public getRowId: GetRowIdFunc<AgGridRowModel> = (params: GetRowIdParams<AgGridRowModel>) =>
    params.data.rowId;

  // Selection
  selectedRows: AgGridRowModel[] = [];
  hasSelectedRows() {
    return this.selectedRows.length > 0;
  }
  get selectedRowsCount() {
    return this.selectedRows.length;
  }

  onSelectionChanged(event: SelectionChangedEvent) {
    this.selectedRows = event.api.getSelectedRows();

    // mimic what updateSelection() was doing
    const selected = this.selectedRows;
    this.rows.forEach(r => r.selected = !isNil(selected.find(s => s === r)));
    this.selectionChange.emit(selected);
  }

  // _allSelected = false;
  // get allSelected(): boolean {
  //   return this._allSelected;
  // }
  // set allSelected(value: boolean) {
  //   this._allSelected = value;
  //   // NOTE: setting values via setSelectedState and not via the selected property, 
  //   // since it has other usage and emits events
  //   if (this.allSelected === true) {
  //     this.rows.forEach(row => {
  //       row.setSelectedState(true);
  //     });
  //   } else if (this.allSelected === false) {
  //     this.rows.forEach(row => {
  //       row.setSelectedState(false);
  //     });
  //   }

  //   this.updateSelection(false);
  // }

  // onSelectedClick(event: MouseEvent, rowIndex: number): void {
  //   // NOTE: setting values via setSelectedState and not via the selected property, 
  //   // since it has other usage and emits events
  //   if (event.shiftKey && this.lastClickedIndex !== null) {
  //     const start = Math.min(this.lastClickedIndex, rowIndex);
  //     const end = Math.max(this.lastClickedIndex, rowIndex);
  //     const selectedState = this.rows[this.lastClickedIndex].selected;

  //     for (let i = start; i <= end; i++) {
  //       this.rows[i].setSelectedState(selectedState);
  //     }
  //   } else {
  //     this.rows[rowIndex].setSelectedState(!this.rows[rowIndex].selected);
  //     this.lastClickedIndex = rowIndex;
  //   }

  //   this.updateSelection();
  // }

  // updateSelection(updateAllSelected?: boolean) {
  //   if (updateAllSelected !== false) {
  //     // NOTE: setting values via the _allSelected property and not via the allSelected property, 
  //     // since it has other usage and calls updateSelection as well
  //     if (this.rows.length) {
  //       const allSelected = this.rows.every(r => r.selected === true);
  //       const allUnSelected = this.rows.every(r => r.selected !== true);
  //       if (allSelected) {
  //         this._allSelected = true;
  //       } else if (allUnSelected) {
  //         this._allSelected = false;
  //       } else {
  //         this._allSelected = undefined;
  //       }
  //     } else {
  //       this._allSelected = false;
  //     }
  //   }

  //   let selectedRows = [];
  //   if (this.rows.length) {
  //     selectedRows = this.rows.filter(r => r.selected === true);
  //   }
  //   // NOTE: we don't want to emit if the value hasn't changed
  //   if ((selectedRows.length === 0 && this.selectedRows.length === 0) ||
  //     isEqual(new Set(selectedRows), new Set(this.selectedRows))) {
  //     return;
  //   }

  //   this.selectedRows = selectedRows;
  //   this.selectionChange.emit(this.selectedRows);
  // }

  onClearSelectionClicked() {
    // this.allSelected = false;
    this.gridApi.deselectAll();
  }

  // Pagination
  goToPage(page: number) {
    if (page >= 1 && page <= this.pagesCount && page !== this.currentPage) {
      this.currentPage = page;
      this.updateCurrentPageFromTo();

      const pageSkip = (this.currentPage - 1) * this.pageSize;
      this.pageSkipChange.emit(pageSkip);
      this.pageChange.emit();
    }
  }

  updatePagination() {
    if (!isNil(this.pageSize) &&
      !isNil(this.totalCount) &&
      !isNil(this.pageSkip) &&
      this.pageSize > 0) {
      this.pagesCount = Math.ceil(this.totalCount / this.pageSize);
      this.currentPage = (this.pageSkip / this.pageSize) + 1;
    } else {
      this.pagesCount = 0;
      this.currentPage = 1;
    }
    this.updateCurrentPageFromTo();
  }

  updateCurrentPageFromTo() {
    if (this.pagesCount > 0) {
      this.currentPageFrom = (this.currentPage * this.pageSize) - this.pageSize + 1;
      if (this.currentPage !== this.pagesCount) {
        this.currentPageTo = this.currentPage * this.pageSize;
      } else {
        this.currentPageTo = this.totalCount;
      }
    } else {
      this.currentPageFrom = 0;
      this.currentPageTo = 0;
    }
  }

  isPositive(x: number) {
    return x !== null && x !== undefined && x > 0;
  }

  // Editing
  get editable(): boolean {
    return this.canEdit || this.canAdd;
  }

  removeRow(row: AgGridRowModel) {
    const deletedRows = this.rows.splice(this.rows.indexOf(row), 1);
    const deletedRow = deletedRows[0];
    deletedRow.destroy();
  }

  onCellClick(cellId: string, row: AgGridRowModel, rowIndex: number) {
    row.$cellClicked(cellId);

    if (this.canEdit && this.canSwitchRow && !row.isEdit) {
      this.setDisplayMode();
      // open clicked cell and try focus
      const cell = row.cells[cellId];
      if (cell.isEditable) {
        row.setEditMode();
        cell.focus();
      }
    } else {
      if (this.canEdit && !this.canSwitchRow) {
        const currentEditingRowIndex = this.rows.findIndex(r => r.isEdit);
        if (currentEditingRowIndex != rowIndex) {
          this.rows[currentEditingRowIndex].setEditMode();
        }
      }
    }
  }

  // onCellKeyDown(event, cellId: string, row: GridRowModel, rowIndex: number) {
  //   // console.log(event);
  //   if (event.repeat !== true && event.key === 'Tab') {
  //     const cell = row.cells[cellId];
  //     if (event.shiftKey === false && row.getLastEditableCell() === cell) {
  //       setTimeout(() => {
  //         if (row.formGroup.valid && !row.$isInAction) {
  //           this.confirmAndMoveNext(row, rowIndex);
  //         } else {
  //           cell.focus();
  //         }
  //       }, 0);
  //     } else if (event.shiftKey === true && row.getFirstEditableCell() === cell) {
  //       setTimeout(() => {
  //         if (row.formGroup.valid && !row.$isInAction) {
  //           this.confirmAndMovePrev(row, rowIndex);
  //         } else {
  //           cell.focus();
  //         }
  //       }, 0);
  //     }
  //   }
  // }

  onCellKeyDown(e) {
    // console.log(e);
    if (!e.event) {
      return;
    }
    const event = e.event as unknown as KeyboardEvent;
    if (event.repeat !== true && event.key === 'Tab') {
      const rowNode: RowNode = e.node;
      const row: AgGridRowModel = rowNode.data;
      const rowIndex = rowNode.rowIndex;
      const column: Column = e.column;
      const cell = row.cells[column.getColId()];

      if (event.shiftKey === false) {
        if (row.getLastEditableCell() === cell) {
          // setTimeout(() => {
          if (row.formGroup.valid && !row.$isInAction) {
            this.confirmAndMoveNext(row, rowIndex);
          } else {
            // cell.focus();

            this.gridApi.setFocusedCell(rowIndex, column);
          }
          // }, 0);
        } else {
          const columns = this.gridApi.getColumns();
          const currentColIndex = columns.findIndex(c => c === column);
          const nextColumn = columns.slice(currentColIndex + 1).find(c => c.isVisible())
          this.gridApi.setFocusedCell(rowIndex, nextColumn);
        }
      } else if (event.shiftKey === true) {
        if (row.getFirstEditableCell() === cell) {
          // setTimeout(() => {
          if (row.formGroup.valid && !row.$isInAction) {
            this.confirmAndMovePrev(row, rowIndex);
          } else {
            // cell.focus();

            this.gridApi.setFocusedCell(rowIndex, column);
          }
          // }, 0);
        }
        else {
          const columns = [...this.gridApi.getColumns()];
          const currentColIndex = columns.findIndex(c => c === column);
          const nextColumn = columns.slice(0, currentColIndex).reverse().find(c => c.isVisible())
          this.gridApi.setFocusedCell(rowIndex, nextColumn);
        }
      }
    }
  }

  async confirmAndMoveNext(row: AgGridRowModel, rowIndex: number) {
    await row.confirm();

    if ((rowIndex + 1) == (this.rows.length)) {
      // exitFromLastCell
      // this.exitFromLastCell.emit();
      this.onCommandAddLineClicked();
    } else if (this.canEdit && this.canSwitchRow) {
      const nextRow = this.rows[rowIndex + 1];
      nextRow.setEditMode();
      nextRow.focusFirstEditableCell();
    }
  }

  async confirmAndMovePrev(row: AgGridRowModel, rowIndex: number) {
    await row.confirm();

    if (rowIndex === 0) {
      // noop
    } else if (this.canEdit && this.canSwitchRow) {
      const prevRow = this.rows[rowIndex - 1];
      prevRow.setEditMode();
      prevRow.focusLastEditableCell();
    }
  }

  setDisplayMode() {
    this.rows.filter(t => t.isEdit === true).forEach((r) => { r.setDisplayMode(); });
  }

  get canSwitchRow(): boolean {
    return !(this.rows.filter(r => r.isEdit === true && r.$isBusy === true).length > 0);
  }

  async onCommandAddLineClicked() {
    if (this.canAdd && this.canSwitchRow && !(this.addLineModel.control.readOnly || this.addLineModel.control.disabled)) {
      this.setDisplayMode();
      const row = await this.addNewRowFn();
      row.gridApi = this.gridApi;
      row.focusFirstEditableCell();

      this.gridApi.setGridOption('rowData', this.rows);
      row.setEditMode();
    }
  }

  commandConfirmClicked(row: AgGridRowModel) {
    row.confirm().then(() => {
      this.gridApi.refreshCells();
    });
  }

  commandCancelNewRowClicked(row: AgGridRowModel) {
    this.removeRow(row);

    this.gridApi.stopEditing(true);
    this.gridApi.setGridOption('rowData', this.rows);
    this.gridApi.refreshCells();
  }
  commandCancelExistingRowClicked(row: AgGridRowModel) {
    row.setDisplayMode();
    row.refresh();
  }

  onExpandRowClicked(row: AgGridRowModel) {
    row.expand = !row.expand;
  }


  onRowEditingStarted(params) {
    // console.log('onRowEditingStarted', params)
    // const row: GridRowModel = params.data;
    // row.setEditMode();
    if (this.canEdit) {
      this.gridApi.setColumnsVisible(['$command'], true);
    }
    if (this.rowCommandTemplateRef) {
      this.gridApi.setColumnsVisible(['$rowOptions'], false);
    }
  }

  onRowEditingStopped(params) {
    // console.log('onRowEditingStopped', params)
    // const row: GridRowModel = params.data;
    // row.setDisplayMode();
    if (this.canEdit) {
      this.gridApi.setColumnsVisible(['$command'], false);
    }
    if (this.rowCommandTemplateRef) {
      this.gridApi.setColumnsVisible(['$rowOptions'], true);
    }
  }

  // getHeaderStyle(column: GridHeaderModel) {
  //   let dynamicStyles;

  //   if (this.containerStyles.columnSizingType === 'headersWidth' && column.width) {
  //     const widthInRem = this.getWidthInRem(column);
  //     dynamicStyles = { 'min-width': `${widthInRem}rem` };
  //   } else if (this.containerStyles.columnSizingType === 'fixedWidth') {
  //     const widthInRem = this.getWidthInRem(column);
  //     dynamicStyles = { 'width': `${widthInRem}rem` };
  //   } else {
  //     dynamicStyles = {};
  //   }

  //   return Object.assign(dynamicStyles, column.styles.style);
  // }

  // getCellStyle(column: GridHeaderModel, cell: GridCellModel<ControlModel, ControlModel | void>) {
  //   let dynamicStyles;

  //   if (this.containerStyles.columnSizingType === 'cellsWidth' && column.width) {
  //     const widthInRem = this.getWidthInRem(column);
  //     dynamicStyles = { 'max-width': `${widthInRem}rem` };
  //   } else {
  //     dynamicStyles = {};
  //   }

  //   return Object.assign(dynamicStyles, cell.styles.style);
  // }

  // get tableStyleComputed() {
  //   let dynamicStyles;
  //   if (this.containerStyles.columnSizingType === 'fixedWidth') {
  //     let totalColumnWidth = 0;
  //     const columns = Object.keys(this.headers).map(i => this.headers[i]);
  //     for (let column of columns) {
  //       const widthInRem = this.getWidthInRem(column);
  //       totalColumnWidth += widthInRem;
  //     }
  //     if (this.selection) {
  //       totalColumnWidth += 2.5; // 2.5rem for checkbox
  //     }
  //     if (this.canEdit) { // TODO
  //       totalColumnWidth += 5;// 5rem for cell-command
  //     }

  //     dynamicStyles = { 'width': `${totalColumnWidth}rem` };
  //   } else {
  //     dynamicStyles = {};
  //   }

  //   return Object.assign(dynamicStyles, {});
  // }

  // getWidthInRem(column) {
  //   return (column.hidden || column.removed)
  //     ? 0
  //     : (column.width ?? 100) / 16;
  // }

  onSortChanged(event: SortChangedEvent<any>) {
    Object.keys(this.headers).forEach(hk => { this.headers[hk].order = null; this.headers[hk].orderIndex = null });
    event.api.getAllGridColumns()
      .filter(item => !!item.getSort())
      .forEach(item => {
        this.headers[item.getId()].order = item.getSort();
        this.headers[item.getId()].orderIndex = item.getSortIndex()
      });
    this.sortChange.emit();
  }

  onFilterChanged(e: FilterChangedEvent) {
    this.filterChange.emit();
  }
}